import type { Paginator } from 'types'

import { normalizePermissionMask } from '../../../utils/fundEmployeePermissions.ts'

export type EmployeeStatus = string

export type EmployeePermissions = {
  funds: bigint
  pools: Record<string, bigint>
}

export type EmployeeApiPermissions = {
  funds?: null | string
  pools?: null | Record<string, null | string>
}

export type EmployeeApiResponse = {
  avatar?: null | string
  email?: null | string
  firstName?: null | string
  fundAddress?: null | string
  fundId?: null | string
  id?: null | string
  lastName?: null | string
  middleName?: null | string
  permissions?: EmployeeApiPermissions | null
  phone?: null | string
  status?: EmployeeStatus | null
  userAddress: string
}

export type Employee = {
  avatar?: null | string
  email?: null | string
  firstName?: null | string
  fundAddress?: null | string
  fundId?: null | string
  id?: null | string
  lastName?: null | string
  middleName?: null | string
  permissions: EmployeePermissions
  phone?: null | string
  status?: EmployeeStatus | null
  userAddress: string
}

export type EmployeesResponse = {
  employees: Array<Employee>
}

export type EmployeesApiResponse = {
  employees?: Array<EmployeeApiResponse>
}

export type CreateEmployeeProfileInput = {
  avatar?: null | string
  email?: null | string
  firstName?: null | string
  lastName?: null | string
  middleName?: null | string
  userAddress: string
}

export type CreateEmployeeProfileBody = {
  avatar: string
  email: string
  firstName: string
  lastName: string
  middleName: string
  userAddress: string
}

export const normalizeEmployeePermissions = (permissions?: EmployeeApiPermissions | null): EmployeePermissions => ({
  funds: normalizePermissionMask(permissions?.funds),
  pools: Object.fromEntries(
    Object.entries(permissions?.pools || {}).map(([address, mask]) => [address, normalizePermissionMask(mask)]),
  ),
})

export const normalizeEmployee = ({ permissions, ...employee }: EmployeeApiResponse): Employee => ({
  ...employee,
  permissions: normalizeEmployeePermissions(permissions),
})

export const normalizeEmployeesResponse = (response: EmployeesApiResponse): EmployeesResponse => ({
  employees: (response.employees || []).map(normalizeEmployee),
})

export const createEmployeeProfileBody = ({
  avatar,
  email,
  firstName,
  lastName,
  middleName,
  userAddress,
}: CreateEmployeeProfileInput): CreateEmployeeProfileBody => ({
  avatar: avatar ?? '',
  email: email ?? '',
  firstName: firstName ?? '',
  lastName: lastName ?? '',
  middleName: middleName ?? '',
  userAddress,
})

export type Fund = {
  createdAt: Date
  description: string
  fundAddress: string
  id: string
  name: string
  status: string
}
export type FundResponse = Paginator & { data: Array<Fund> }
